import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Form, FormField } from '../types/form';
import { ArrowLeft, Plus, Trash2, ChevronDown, ChevronRight, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { formsApi } from '../api/api';
import CustomDatePicker from '../components/CustomDatePicker';
import CustomFileUpload from '../components/CustomFileUpload';

const CreateLink: React.FC = () => {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();

  const [form, setForm] = useState<Form | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  
  const [linkName, setLinkName] = useState('');
  const [maxUses, setMaxUses] = useState(1);
  const [preSelectedFields, setPreSelectedFields] = useState<Record<string, any>>({});
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    basic: true,
    preselect: true
  });

  const fetchForm = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await formsApi.getForm(formId!);
      setForm(response.data);
    } catch (error) {
      toast.error('Failed to load form');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [formId, navigate]);

  useEffect(() => {
    if (formId) {
      fetchForm();
    }
  }, [formId, fetchForm]);

  const handleSubmit = async () => {
    if (!linkName.trim()) {
      toast.error('Please enter a link name');
      return;
    }

    try {
      setCreating(true);
      await formsApi.createOneUseLink(formId!, linkName.trim(), maxUses, preSelectedFields);
      toast.success('Link created successfully');
      navigate(`/forms/${formId}/edit?tab=links`);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to create link';
      toast.error(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const handleFieldValueChange = (fieldId: string, value: any) => {
    setPreSelectedFields(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const addPreSelectedField = (fieldId: string) => {
    const field = form?.fields.find(f => f.id === fieldId);
    let initialValue: any = '';
    
    if (field?.field_type === 'multi_row') {
      const columns = (field.options as any)?.columns || [];
      const initialRowCount = (field.options as any)?.initialRowCount || 1;
      initialValue = Array(initialRowCount).fill(null).map(() => columns.map(() => ''));
    } else if (field?.field_type === 'checkbox_with_input') {
      initialValue = {};
    }
    
    setPreSelectedFields(prev => ({
      ...prev,
      [fieldId]: initialValue
    }));
  };

  const removePreSelectedField = (fieldId: string) => {
    setPreSelectedFields(prev => {
      const newFields = { ...prev };
      delete newFields[fieldId];
      return newFields;
    });
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const renderFieldInput = (field: FormField) => {
    const value = preSelectedFields[field.id] || '';

    switch (field.field_type) {
      case 'text':
      case 'email':
      case 'phone':
        return (
          <Input
            value={value}
            onChange={(e) => handleFieldValueChange(field.id, e.target.value)}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
            className="w-full"
          />
        );

      case 'number':
        return (
          <Input
            type="number"
            value={value}
            onChange={(e) => handleFieldValueChange(field.id, e.target.value)}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
            className="w-full"
          />
        );

      case 'textarea':
        return (
          <textarea
            className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground resize-none"
            rows={3}
            value={value}
            onChange={(e) => handleFieldValueChange(field.id, e.target.value)}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
          />
        );

      case 'dropdown':
        return (
          <select
            className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground"
            value={value}
            onChange={(e) => handleFieldValueChange(field.id, e.target.value)}
          >
            <option value="">Select an option</option>
            {Object.entries(field.options || {}).map(([key, optionValue]) => (
              <option key={key} value={key}>
                {optionValue as string}
              </option>
            ))}
          </select>
        );

      case 'radio':
        return (
          <div className="space-y-2">
            {Object.entries(field.options || {}).map(([key, optionValue]) => (
              <label key={key} className="flex items-center space-x-2">
                <input
                  type="radio"
                  name={`radio-${field.id}`}
                  value={key}
                  checked={value === key}
                  onChange={(e) => handleFieldValueChange(field.id, e.target.value)}
                  className="text-primary focus:ring-ring"
                />
                <span className="text-sm">{optionValue as string}</span>
              </label>
            ))}
          </div>
        );

      case 'checkbox':
        return (
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={value === true}
              onChange={(e) => handleFieldValueChange(field.id, e.target.checked)}
              className="text-primary focus:ring-ring"
            />
            <span className="text-sm">{field.label}</span>
          </label>
        );

      case 'date':
        return (
          <CustomDatePicker
            value={value}
            onChange={(date) => handleFieldValueChange(field.id, date)}
            placeholder={field.placeholder || 'Select date'}
          />
        );

      case 'file':
        return (
          <CustomFileUpload
            onChange={(file) => handleFieldValueChange(field.id, file)}
            placeholder={field.placeholder || 'Choose file'}
          />
        );

      case 'multi_row':
        const columns = (field.options as any)?.columns || [];
        const rows = Array.isArray(value) ? value : [];
        
        return (
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground mb-2">
              Configure pre-filled rows and columns:
            </div>
            {columns.length > 0 ? (
              <div className="space-y-2">
                <div className="grid gap-2" style={{gridTemplateColumns: `repeat(${columns.length + 1}, 1fr)`}}>
                  <div className="font-medium text-sm">Row</div>
                  {columns.map((col: any, colIndex: number) => (
                    <div key={colIndex} className="font-medium text-sm">{col.label}</div>
                  ))}
                </div>
                {rows.map((row: any[], rowIndex: number) => (
                  <div key={rowIndex} className="grid gap-2" style={{gridTemplateColumns: `repeat(${columns.length + 1}, 1fr)`}}>
                    <div className="text-sm text-muted-foreground flex items-center">{rowIndex + 1}</div>
                    {columns.map((col: any, colIndex: number) => (
                      <div key={colIndex}>
                        {(col.type === 'text' || col.type === 'email' || col.type === 'phone') && (
                          <Input
                            type={col.type === 'email' ? 'email' : col.type === 'phone' ? 'tel' : 'text'}
                            value={row[colIndex] || ''}
                            onChange={(e) => {
                              const newRows = [...rows];
                              newRows[rowIndex][colIndex] = e.target.value;
                              handleFieldValueChange(field.id, newRows);
                            }}
                            placeholder={col.placeholder || ''}
                            className="text-sm"
                          />
                        )}
                        {col.type === 'number' && (
                          <Input
                            type="number"
                            value={row[colIndex] || ''}
                            onChange={(e) => {
                              const newRows = [...rows];
                              newRows[rowIndex][colIndex] = e.target.value;
                              handleFieldValueChange(field.id, newRows);
                            }}
                            placeholder={col.placeholder || ''}
                            className="text-sm"
                          />
                        )}
                        {col.type === 'dropdown' && (
                          <select
                            value={row[colIndex] || ''}
                            onChange={(e) => {
                              const newRows = [...rows];
                              newRows[rowIndex][colIndex] = e.target.value;
                              handleFieldValueChange(field.id, newRows);
                            }}
                            className="w-full px-2 py-1 text-sm border border-border rounded-md bg-background"
                          >
                            <option value="">Select...</option>
                            {(col.options || []).map((option: string, optIndex: number) => (
                              <option key={optIndex} value={option}>{option}</option>
                            ))}
                          </select>
                        )}
                        {col.type === 'checkbox' && (
                          <div className="space-y-1">
                            {(col.options || []).map((option: string, optIndex: number) => {
                              const selectedValues = Array.isArray(row[colIndex]) ? row[colIndex] : [];
                              return (
                                <label key={optIndex} className="flex items-center space-x-1 text-xs">
                                  <input
                                    type="checkbox"
                                    checked={selectedValues.includes(option)}
                                    onChange={(e) => {
                                      const newRows = [...rows];
                                      const currentValues = Array.isArray(newRows[rowIndex][colIndex]) ? newRows[rowIndex][colIndex] : [];
                                      if (e.target.checked) {
                                        newRows[rowIndex][colIndex] = [...currentValues, option];
                                      } else {
                                        newRows[rowIndex][colIndex] = currentValues.filter((v: string) => v !== option);
                                      }
                                      handleFieldValueChange(field.id, newRows);
                                    }}
                                    className="text-xs"
                                  />
                                  <span>{option}</span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
                <div className="flex justify-start">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newRows = [...rows, columns.map(() => '')];
                      handleFieldValueChange(field.id, newRows);
                    }}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Row
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">No columns configured for this multi-row field</div>
            )}
          </div>
        );

      case 'checkbox_with_input':
        const checkboxValue = value || {};
        
        return (
          <div className="space-y-3">
            {Object.entries(field.options || {}).filter(([key]) => key !== 'inputType' && key !== 'inputPlaceholder').map(([key, optionValue]) => (
              <div key={key} className="flex items-center space-x-3">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={checkboxValue[key]?.checked || false}
                    onChange={(e) => {
                      const newValue = {
                        ...checkboxValue,
                        [key]: {
                          checked: e.target.checked,
                          value: checkboxValue[key]?.value || ''
                        }
                      };
                      handleFieldValueChange(field.id, newValue);
                    }}
                    className="text-primary focus:ring-ring"
                  />
                  <span className="text-sm">{optionValue as string}</span>
                </label>
                {checkboxValue[key]?.checked && (
                  <Input
                    type={(field.options as any)?.inputType || 'text'}
                    value={checkboxValue[key]?.value || ''}
                    onChange={(e) => {
                      const newValue = {
                        ...checkboxValue,
                        [key]: {
                          checked: true,
                          value: e.target.value
                        }
                      };
                      handleFieldValueChange(field.id, newValue);
                    }}
                    placeholder={(field.options as any)?.inputPlaceholder || 'Enter value'}
                    className="flex-1"
                  />
                )}
              </div>
            ))}
          </div>
        );

      default:
        return (
          <Input
            value={value}
            onChange={(e) => handleFieldValueChange(field.id, e.target.value)}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
            className="w-full"
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-6">
          <div className="text-center">
            <p className="text-muted-foreground">Loading form...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-6">
          <div className="text-center">
            <p className="text-destructive">Form not found</p>
          </div>
        </div>
      </div>
    );
  }

  const preSelectedFieldsCount = Object.keys(preSelectedFields).length;

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/forms/${formId}/edit?tab=links`)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Links
              </Button>
              <div>
                <h1 className="text-xl font-semibold">Create New Link</h1>
                <p className="text-sm text-muted-foreground">{form.title}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="outline" onClick={() => navigate(`/forms/${formId}/edit?tab=links`)}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!linkName.trim() || creating}
              >
                {creating ? 'Creating...' : 'Create Link'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <div className="max-w-4xl mx-auto space-y-6">
          <Card>
            <CardHeader>
              <button
                onClick={() => toggleSection('basic')}
                className="flex items-center gap-2 text-left w-full group"
              >
                {expandedSections.basic ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
                <CardTitle className="group-hover:text-foreground/80">Basic Settings</CardTitle>
              </button>
            </CardHeader>

            {expandedSections.basic && (
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Link Name <span className="text-destructive">*</span>
                    </label>
                    <Input
                      value={linkName}
                      onChange={(e) => setLinkName(e.target.value)}
                      placeholder="Enter recipient name or description"
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      A descriptive name to help you identify this link
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Maximum Uses
                    </label>
                    <Input
                      type="number"
                      min="1"
                      max="999"
                      value={maxUses}
                      onChange={(e) => setMaxUses(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      How many times this link can be used before it expires
                    </p>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>

          {form.fields.length > 0 && (
            <Card>
              <CardHeader>
                <button
                  onClick={() => toggleSection('preselect')}
                  className="flex items-center gap-2 text-left w-full group"
                >
                  {expandedSections.preselect ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                  <CardTitle className="group-hover:text-foreground/80">
                    Pre-selected Fields
                    {preSelectedFieldsCount > 0 && (
                      <span className="ml-2 px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                        {preSelectedFieldsCount}
                      </span>
                    )}
                  </CardTitle>
                </button>
                <p className="text-sm text-muted-foreground">
                  Pre-fill form fields for users who access this link. These fields will be automatically filled when they open the form.
                </p>
              </CardHeader>

              {expandedSections.preselect && (
                <CardContent className="space-y-6">
                  {form.fields
                    .filter(field => field.field_type !== 'logic') // Hide logic fields in pre-selected fields
                    .map((field) => {
                    const isPreSelected = field.id in preSelectedFields;
                    
                    return (
                      <div key={field.id} className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Settings className="h-4 w-4 text-muted-foreground" />
                            <label className="font-medium">
                              {field.label}
                              {field.is_required && <span className="text-destructive ml-1">*</span>}
                            </label>
                            <span className="text-xs text-muted-foreground">({field.field_type})</span>
                          </div>
                          {isPreSelected ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => removePreSelectedField(field.id)}
                              className="text-destructive hover:text-destructive-foreground hover:bg-destructive"
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              Remove
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => addPreSelectedField(field.id)}
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Add Pre-fill
                            </Button>
                          )}
                        </div>

                        {isPreSelected && (
                          <div className="pl-6 border-l-2 border-primary/20 bg-muted/20 p-4 rounded-r-lg">
                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-muted-foreground">
                                Pre-filled value:
                              </label>
                              {renderFieldInput(field)}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {form.fields.length > 0 && preSelectedFieldsCount === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Settings className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No fields pre-selected yet.</p>
                      <p className="text-sm">Click "Add Pre-fill" next to any field to pre-fill it for this link.</p>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateLink;